import { Suspense, useCallback, useMemo, useState } from "react";
import { usePaginationFragment, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";

import { useLoadQuery, usePopoverPaginationInfiniteScroll } from "@skydio/relay-toolkit";
import { ComboBox, useDebouncedState, useFilteredItemsWithMemory } from "@skydio/rivet-ui";

import type { PreloadedQuery } from "react-relay";
import type { Key } from "@skydio/rivet-ui";
import type {
  SalesforceAccountSelector_accounts$data,
  SalesforceAccountSelector_accounts$key,
} from "__generated__/SalesforceAccountSelector_accounts.graphql";
import type { SalesforceAccountSelectorQuery } from "__generated__/SalesforceAccountSelectorQuery.graphql";
import type { ConnectionNodeType } from "components/common/Pagination";

const paginationFragment = graphql`
  fragment SalesforceAccountSelector_accounts on Salesforce
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "SalesforceAccountSelectorRefetchQuery") {
    accounts(searchTerm: $searchTerm, first: $count, after: $cursor)
      @connection(key: "SalesforceAccountSelector_accounts") {
      edges {
        node {
          id
          accountId
          name
          orgTier
          segmentGroup
        }
      }
    }
  }
`;

const query = graphql`
  query SalesforceAccountSelectorQuery($count: Int!, $cursor: String, $searchTerm: String) {
    salesforce {
      ...SalesforceAccountSelector_accounts
        @arguments(count: $count, cursor: $cursor, searchTerm: $searchTerm)
    }
  }
`;

export type SalesforceAccount = ConnectionNodeType<
  SalesforceAccountSelector_accounts$data["accounts"]
>;

const PAGE_SIZE = 10;

interface Props {
  onSelectionChange?: (keys: Set<Key>) => void;
  onSelectedAccountChange?: (account: SalesforceAccount) => void;
}

interface InternalProps {
  queryReference: PreloadedQuery<SalesforceAccountSelectorQuery>;
}

const SalesforceAccountSelectorInternal = ({
  queryReference,
  onSelectionChange,
  onSelectedAccountChange,
}: InternalProps & Props) => {
  const [inputValue, setSearchTerm, { debouncedValue: searchTerm }] = useDebouncedState("");
  const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(new Set());

  const queryData = usePreloadedQuery(query, queryReference);

  const paginationData = usePaginationFragment<
    SalesforceAccountSelectorQuery,
    SalesforceAccountSelector_accounts$key
  >(paginationFragment, queryData.salesforce);

  const searchOptions = useMemo(() => ({ searchTerm }), [searchTerm]);

  const [infiniteScrollProps, currentItems] = usePopoverPaginationInfiniteScroll(
    paginationData,
    data => data.accounts,
    PAGE_SIZE,
    searchOptions
  );

  // This remembers previously selected items even if they are out of view of the current search
  const itemProps = useFilteredItemsWithMemory({
    currentItems,
    selectedKeys,
    getKey: node => node.accountId,
  });

  const handleSelectionChange = useCallback(
    (keys: Set<Key>) => {
      setSelectedKeys(keys);
      onSelectionChange?.(keys);
      const account = currentItems.find(item => item.accountId === [...keys][0]);
      if (account) {
        onSelectedAccountChange?.(account);
      }
    },
    [setSelectedKeys, onSelectionChange, currentItems, onSelectedAccountChange]
  );

  return (
    <ComboBox
      placeholder="Search"
      inputValue={inputValue}
      onInputChange={setSearchTerm}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      selectionMode="single"
      className="w-full"
      {...itemProps}
      {...infiniteScrollProps}
    >
      {account => (
        <ComboBox.Item key={account.accountId} description={account.accountId}>
          {account.name}
        </ComboBox.Item>
      )}
    </ComboBox>
  );
};

const SelectorSkeleton = () => {
  return (
    <ComboBox placeholder="Loading..." className="w-full" isLoading>
      <></>
    </ComboBox>
  );
};

const SalesforceAccountSelector = (props: Props) => {
  const [queryReference] = useLoadQuery<SalesforceAccountSelectorQuery>(query, {
    count: PAGE_SIZE,
    searchTerm: null,
  });

  const suspenseFallback = useMemo(() => <SelectorSkeleton />, []);

  return (
    <div>
      {queryReference ? (
        <Suspense fallback={suspenseFallback}>
          <SalesforceAccountSelectorInternal queryReference={queryReference} {...props} />
        </Suspense>
      ) : (
        <SelectorSkeleton />
      )}
    </div>
  );
};

export default SalesforceAccountSelector;
